import React, { ReactNode, useEffect, useId, useState } from "react";
import { ThemeWrapper } from "@verity/components";
import { CustomSettingData } from "../../settings-page";
import apiClient from "../../../../helpers/api-client";
import { Icon, Skeleton, Typography } from "@mui/material";
import {
	CoreButton,
	CoreCheckbox,
	CoreModal,
	CoreRadioGroup,
	CoreSpinner,
	CoreTable,
	CoreTextField,
	coreToast,
	RadioOption,
} from "@verity/components/core";
import { generateLmsApiToken } from "../../../../helpers/lms-api-token";
import WarningIcon from "../../../../images/warning.svg?react";
import i18n from "../../../../i18n";

const DEFAULT_SUFFIX = "YuJa Verity Test Proctoring";
const SUFFIX_CHARACTER_LIMIT = 40;

type SuffixOption = "default" | "custom" | "none";

function getSuffixOptionFromText(suffix: string | null): SuffixOption {
	if (suffix === DEFAULT_SUFFIX) {
		return "default";
	} else if (suffix === "" || !suffix) {
		return "none";
	} else {
		return "custom";
	}
}

const SuffixRadioOption = ({
	selectedOption,
	disabled,
	customSuffix,
	setCustomSuffix,
}: {
	selectedOption: SuffixOption;
	disabled?: boolean;
	customSuffix: string | null;
	setCustomSuffix: (value: string) => void;
}) => {
	const labelId = useId();
	return (
		<div style={{ display: "flex", alignItems: "center" }}>
			<Typography id={labelId} fontSize={14} sx={{ marginRight: "12px" }}>
				{i18n.t("portal.components.settings.suffix.IndexTSX.changeSuffixLabel")}
			</Typography>
			<CoreTextField
				disabled={selectedOption !== "custom" || disabled}
				value={customSuffix}
				onChange={(e) => selectedOption === "custom" && setCustomSuffix(e.target.value)}
				inputProps={{
					style: { fontSize: "14px", padding: "8px" },
					maxLength: SUFFIX_CHARACTER_LIMIT,
					"aria-labelledby": labelId,
				}}
			/>
		</div>
	);
};

export const SuffixInput = (props: {
	loadData: { suffix: string | null };
	setting: CustomSettingData<string | null>;
	onSettingChange: (value?: string | null) => void;
	disabled?: boolean;
	instituteId?: number;
	lms?: string;
	ariaLabelledByTitleId?: string;
}) => {
	const [oldSuffix] = React.useState(props.loadData.suffix);
	const [newSuffix, setNewSuffix] = React.useState(props.setting.value ?? props.loadData.suffix);
	const [selectedOption, setSelectedOption] = React.useState<SuffixOption>(getSuffixOptionFromText(newSuffix));
	const [customSuffix, setCustomSuffix] = React.useState(selectedOption === "custom" ? newSuffix : "");
	// const [canUpdateLmsAssessmentNames, setCanUpdateLmsAssessmentNames] = React.useState(false);

	useEffect(() => {
		props.onSettingChange(newSuffix);
	}, [newSuffix, props]);

	useEffect(() => {
		if (selectedOption === "default") {
			setNewSuffix(DEFAULT_SUFFIX);
		} else if (selectedOption === "none") {
			setNewSuffix(null);
		} else {
			setNewSuffix(customSuffix);
		}
	}, [selectedOption, customSuffix]);

	// Checks if Canvas theme is applied to parent window.
	const [showThemeWarning, setShowThemeWarning] = React.useState(false);

	useEffect(() => {
		const themeOrigin = document.location.ancestorOrigins?.[0] || document.referrer.replace(/\/$/, "") || "";
		let inIframe = themeOrigin !== document.location.origin;
		try {
			inIframe ||= window.self !== window.top;
		} catch (e) {
			inIframe = true;
		}
		if (inIframe) {
			let timeout: ReturnType<typeof setTimeout> | undefined = undefined;

			const listener = (ev: MessageEvent<unknown>) => {
				if (ev.origin !== themeOrigin || typeof ev.data !== "object" || !ev.data) {
					return;
				}
				if ("type" in ev.data && ev.data.type === "sendInstituteId") {
					setShowThemeWarning(!("instituteId" in ev.data && typeof ev.data.instituteId === "number"));
					window.removeEventListener("message", listener);
					clearTimeout(timeout);
				}
			};
			window.addEventListener("message", listener);
			window.parent.postMessage(
				{
					type: "getInstituteId",
				},
				themeOrigin,
			);

			timeout = setTimeout(() => {
				window.removeEventListener("message", listener);
				setShowThemeWarning(true);
			}, 2000);
		}
	}, []);

	// useEffect(() => {
	// 	void (async () => {
	// 		const instituteInfo = await apiClient.lmsInfo.getInstituteLmsInfo();
	// 		if (instituteInfo?.origin && instituteInfo.lmsApiClientId && instituteInfo.lmsApiClientId.trim() !== "") {
	// 			setCanUpdateLmsAssessmentNames(true);
	// 		}
	// 	})();
	// }, []);

	const [nameModalOpen, setNameModalOpen] = React.useState(false);

	const radioOptions: RadioOption[] = [
		{
			value: "default",
			label: `YuJa Verity Test Proctoring (${i18n.t("portal.components.settings.suffix.IndexTSX.default")})`,
			disabled: props.disabled,
		},
		{
			value: "custom",
			label: (
				<SuffixRadioOption
					selectedOption={selectedOption}
					disabled={props.disabled}
					customSuffix={customSuffix}
					setCustomSuffix={setCustomSuffix}
				/>
			),
			disabled: props.disabled,
		},
		{
			value: "none",
			label: i18n.t("portal.components.settings.suffix.IndexTSX.none"),
			disabled: props.disabled,
		},
	];

	return (
		<ThemeWrapper>
			<div style={{ maxWidth: "100%", overflowX: "auto" }}>
				<div style={{ minWidth: "330px" }}>
					<div style={{ fontSize: "15px" }}>
						<Typography>{i18n.t("portal.components.settings.suffix.IndexTSX.description")}</Typography>
						{showThemeWarning && (
							<div style={{ paddingTop: "12px" }}>
								<WarningContainer>
									<span>
										{i18n.t("portal.components.settings.suffix.IndexTSX.visualizerRequired", {
											lmsName: props.lms === "canvas" ? "Canvas " : "",
										})}
										<a
											href={
												props.lms === "canvas"
													? "https://support.yuja.com/hc/en-us/articles/7332584857623-Integrating-YuJa-Verity-for-Test-Proctoring-into-Canvas#h_01HCJH9QWSFKKFBYSG3TX75XM1"
													: ""
											}
											target="_blank"
											rel="noreferrer"
										>
											{i18n.t("portal.components.settings.suffix.IndexTSX.learnMore")}
										</a>
										{`.`}
									</span>
								</WarningContainer>
							</div>
						)}
					</div>
					<div style={{ paddingTop: "13px", paddingLeft: "2px" }}>
						<CoreRadioGroup
							formControlLabelProps={{ sx: { marginLeft: 0 } }}
							radioLabelFontSize={14}
							radioProps={{ sx: { padding: "2px", marginRight: "4px" } }}
							radioOptions={radioOptions}
							onChange={(e) => setSelectedOption(e.target.value as SuffixOption)}
							coreRadioSize={16}
							radioGroupAriaLabelledby={props.ariaLabelledByTitleId}
							selectedValue={selectedOption}
						/>
					</div>
					{/* <Tooltip
						title={!canUpdateLmsAssessmentNames && `LMS API access is required to apply suffix changes to assessment.`}
					>
						<span>
							<CoreButton
								onClick={() => setNameModalOpen(true)}
								disabled={
									!canUpdateLmsAssessmentNames ||
									props.disabled ||
									!instituteId ||
									!Number.isInteger(instituteId) ||
									oldSuffix === newSuffix ||
									!(oldSuffix || newSuffix)
								}
							>
								Update Suffix
							</CoreButton>
						</span>
					</Tooltip> */}
				</div>
			</div>
			{props.instituteId != null && Number.isInteger(props.instituteId) && (
				<NameModal
					open={nameModalOpen}
					onClose={() => setNameModalOpen(false)}
					oldSuffix={oldSuffix}
					newSuffix={newSuffix}
					instituteId={props.instituteId}
					showThemeWarning={showThemeWarning}
				/>
			)}
		</ThemeWrapper>
	);
};

export async function saveSuffix(suffix?: string | null) {
	try {
		if (suffix === null || typeof suffix === "string") {
			await apiClient.institute.saveSuffix(suffix);
		}
		return { ok: true };
	} catch (error) {
		console.error(error);
		return { ok: false, reason: "Failed to save suffix" };
	}
}

type Course = { id: number; name?: string | null; code?: string | null; courseId: string };
type AssessmentNameResponse = { oldName?: string | null; newName?: string | null; ok: boolean; loading: boolean };
type CourseAssessmentNameResponses = { loading: boolean; assessments: Record<number, AssessmentNameResponse> };

function updateAssessmentNameSuffixesWithCancel({
	oldSuffix,
	newSuffix,
	courses,
	courseAssessmentsMap,
	setCourseAssessmentsMap,
	setCanRetry,
}: {
	oldSuffix: string | null;
	newSuffix: string | null;
	courses: {
		id: number;
		lmsProvidedCourseId: string | undefined;
	}[];
	courseAssessmentsMap: Map<number, CourseAssessmentNameResponses>;
	setCourseAssessmentsMap: (map: Map<number, CourseAssessmentNameResponses>) => void;
	setCanRetry: (canRetry: boolean) => void;
}) {
	let stop = false;

	async function updateAssessmentNameSuffixes() {
		for (const course of courses) {
			if (course.lmsProvidedCourseId) {
				const prev = courseAssessmentsMap.get(course.id)?.assessments;
				const assessmentNames: Record<number, AssessmentNameResponse> = prev ?? {};
				courseAssessmentsMap.set(course.id, { loading: true, assessments: assessmentNames });
				setCourseAssessmentsMap(new Map(courseAssessmentsMap));

				const assessments = (await apiClient.assessment.getCourseAssessments(course.lmsProvidedCourseId))
					.filter(
						(x) =>
							!(typeof x.quizInfo === "object" ? x.quizInfo?.name : null)?.startsWith("Third Party: ") && !x.isDeleted,
					)
					.sort((a, b) =>
						(typeof a.quizInfo === "object" ? (a.quizInfo?.name ?? "") : "")
							.toLowerCase()
							.localeCompare((typeof b.quizInfo === "object" ? (b.quizInfo?.name ?? "") : "").toLowerCase()),
					);

				assessments.forEach((a) => {
					if (!assessmentNames[a.id]?.ok) {
						assessmentNames[a.id] = {
							ok: false,
							loading: true,
							oldName: typeof a.quizInfo === "object" ? a.quizInfo?.name : undefined,
						};
					}
				});
				setCourseAssessmentsMap(new Map(courseAssessmentsMap));

				for (const assessment of assessments) {
					if (!assessmentNames[assessment.id]?.ok) {
						const name = await apiClient.assessment
							.updateLmsAssessmentNameSuffix({
								assessmentId: assessment.id,
								oldSuffix,
								newSuffix,
							})
							.catch((e) => {
								console.error(e);
								return null;
							});
						const success = !!name && (name.ok || !name.exists);
						assessmentNames[assessment.id] = {
							...assessmentNames[assessment.id],
							ok: success,
							loading: false,
							newName: name?.newName ?? null,
						};

						if (!success) {
							setCanRetry(true);
							coreToast.error(i18n.t("portal.components.settings.suffix.IndexTSX.updateFailedMsg"));
						}

						setCourseAssessmentsMap(new Map(courseAssessmentsMap));
					}
					if (stop) {
						break;
					}
				}

				courseAssessmentsMap.set(course.id, { loading: false, assessments: assessmentNames });
				setCourseAssessmentsMap(new Map(courseAssessmentsMap));
			}
			if (stop) {
				break;
			}
		}
	}

	return {
		cancel: () => (stop = true),
		promise: updateAssessmentNameSuffixes(),
	};
}

function NameModal({
	open,
	onClose,
	oldSuffix,
	newSuffix,
	instituteId,
	showThemeWarning,
}: {
	open: boolean;
	onClose: () => void;
	oldSuffix: string | null;
	newSuffix: string | null;
	instituteId: number;
	showThemeWarning: boolean;
}) {
	const [isLoading, setLoading] = useState(false);
	const [isError, setError] = useState(false);
	const [courseMap, setCourseMap] = useState(new Map<number, Course>());

	const [selectedCourseSet, setSelectedCourseSet] = useState(new Set<number>());
	const [courseAssessmentsMap, setCourseAssessmentsMap] = useState(new Map<number, CourseAssessmentNameResponses>());
	const [isLoadingAssessments, setLoadingAssessments] = useState(false);

	const [updateStep, setUpdateStep] = useState(false);
	const [canRetry, setCanRetry] = useState(false);

	const [cancelUpdateSuffixes, setCancelUpdateSuffixes] = useState<() => void>();

	useEffect(() => {
		void (async () => {
			if (open) {
				setUpdateStep(false);
				setLoading(true);
				const courses = await apiClient.course.getInstituteCoursesInfo(instituteId);
				setCourseMap(new Map(courses.map((x) => [x.id, x])));
				setLoading(false);
				setCanRetry(false);
			}
		})();
	}, [open, instituteId]);

	useEffect(() => {
		setCourseAssessmentsMap(new Map());
	}, [oldSuffix, newSuffix]);

	const close = () => {
		cancelUpdateSuffixes?.();
		setCancelUpdateSuffixes(undefined);
		onClose();
		setLoading(false);
		setError(false);
		setLoadingAssessments(false);
	};

	const update = async () => {
		setLoadingAssessments(true);
		await generateLmsApiToken("canvas");

		const courses = Array.from(selectedCourseSet.values())
			.map((c) => ({ id: c, lmsProvidedCourseId: courseMap.get(c)?.courseId }))
			.sort((a, b) =>
				(a.lmsProvidedCourseId ?? "").toLowerCase().localeCompare((b.lmsProvidedCourseId ?? "").toLowerCase()),
			);

		const { cancel, promise } = updateAssessmentNameSuffixesWithCancel({
			oldSuffix,
			newSuffix,
			courses,
			courseAssessmentsMap,
			setCourseAssessmentsMap,
			setCanRetry,
		});

		setCancelUpdateSuffixes(() => cancel);
		await promise;
		setCancelUpdateSuffixes(undefined);

		setLoadingAssessments(false);
	};

	return (
		<CoreModal
			open={open}
			onClose={close}
			title={i18n.t("portal.components.settings.suffix.IndexTSX.updateSuffixLabel")}
			widthFixed
			minWidth="min(85%, 900px)"
			contentMatchModalHeight
			buttons={[
				{
					text: i18n.t("portal.components.settings.suffix.IndexTSX.cancel"),
					color: "secondary",
					onClick: close,
				},
				{
					text: i18n.t("portal.components.settings.suffix.IndexTSX.updateAndsave"),
					hide: updateStep,
					disabled: !selectedCourseSet.size,
					onClick: async () => {
						setUpdateStep(true);
						await saveSuffix(newSuffix);
						await update();
					},
				},
				{
					text: isLoadingAssessments
						? i18n.t("portal.components.settings.suffix.IndexTSX.updating")
						: i18n.t("portal.components.settings.suffix.IndexTSX.finished"),
					hide: !updateStep,
					disabled: isLoadingAssessments,
					onClick: close,
				},
			]}
		>
			<div style={{ maxHeight: "100%", display: "flex", flexDirection: "column" }}>
				{!updateStep ? (
					<>
						<div style={{ paddingBottom: "12px", fontSize: "15px", width: "100%" }}>
							<div style={{ paddingBottom: "6px" }}>
								{!oldSuffix && newSuffix
									? i18n.t("portal.components.settings.suffix.IndexTSX.suffixUpdate.add", { suffix: newSuffix })
									: oldSuffix && !newSuffix
										? i18n.t("portal.components.settings.suffix.IndexTSX.suffixUpdate.remove", { suffix: oldSuffix })
										: oldSuffix && newSuffix
											? i18n.t("portal.components.settings.suffix.IndexTSX.suffixUpdate.update", {
													old: oldSuffix,
													new: newSuffix,
												})
											: i18n.t("portal.components.settings.suffix.IndexTSX.suffixUpdate.none")}
							</div>
							{showThemeWarning && (
								<WarningContainer>
									{i18n.t("portal.components.settings.suffix.IndexTSX.LMSRequireSuffix")}
								</WarningContainer>
							)}
						</div>
						<div style={{ flex: "1 1 100%", minHeight: "0px", display: "flex", flexDirection: "column" }}>
							{!isError && isLoading ? (
								<Skeleton variant="rounded" width="100%" height="496px" />
							) : (
								<CourseSelectTable
									courseList={Array.from(courseMap.values())}
									selectedCourseSet={selectedCourseSet}
									setSelectedCourseSet={setSelectedCourseSet}
								/>
							)}
							{(isError || courseMap.size < 1) && (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										placeContent: "center",
										flexDirection: "column",
										border: "1px solid #e4e4e4",
										borderRadius: "8px",
										padding: "8px",
									}}
								>
									<div style={{ color: "#fff" }}>
										<p
											style={{
												fontSize: "14px",
												textAlign: "center",
												margin: "0px",
												lineHeight: "1",
												color: "#525252",
											}}
										>
											{isError && <div>{i18n.t("portal.components.settings.suffix.IndexTSX.getCoursesFailed")}</div>}
											{!isError && courseMap.size < 1 && (
												<div>{i18n.t("portal.components.settings.suffix.IndexTSX.noCoursesFound")}</div>
											)}
										</p>
									</div>
								</div>
							)}
						</div>
					</>
				) : (
					<>
						<div
							style={{
								paddingBottom: "12px",
								fontSize: "15px",
								display: "flex",
								justifyContent: "space-between",
							}}
						>
							<div style={{ alignSelf: "start", flex: 1 }}>
								<div style={{ paddingBottom: "6px" }}>
									{!oldSuffix &&
										newSuffix &&
										i18n.t("portal.components.settings.suffix.IndexTSX.suffixStatus.add", {
											suffix: newSuffix,
											status: isLoadingAssessments ? "is being" : "has been",
										})}

									{oldSuffix &&
										!newSuffix &&
										i18n.t("portal.components.settings.suffix.IndexTSX.suffixStatus.remove", {
											suffix: oldSuffix,
											status: isLoadingAssessments ? "is being" : "has been",
										})}

									{oldSuffix &&
										newSuffix &&
										i18n.t("portal.components.settings.suffix.IndexTSX.suffixStatus.update", {
											old: oldSuffix,
											new: newSuffix,
											status: isLoadingAssessments ? "is being" : "has been",
										})}
								</div>
								{isLoadingAssessments && (
									<WarningContainer>
										{i18n.t("portal.components.settings.suffix.IndexTSX.waitWarningMsg")}
									</WarningContainer>
								)}
							</div>

							{!isLoadingAssessments && canRetry && (
								<div style={{ alignSelf: "end" }}>
									<CoreButton onClick={update}>{i18n.t("portal.components.settings.suffix.IndexTSX.retry")}</CoreButton>
								</div>
							)}
						</div>
						<div style={{ flex: "1 1 100%", minHeight: "0px", display: "flex", flexDirection: "column" }}>
							<CourseAssessmentViewTable
								courseList={Array.from(courseMap.values())}
								selectedCourseSet={selectedCourseSet}
								courseAssessmentsMap={courseAssessmentsMap}
								loading={isLoadingAssessments}
							/>
						</div>
					</>
				)}
			</div>
		</CoreModal>
	);
}

function CourseSelectTable({
	courseList,
	selectedCourseSet,
	setSelectedCourseSet,
}: {
	courseList: Course[];
	selectedCourseSet: Set<number>;
	setSelectedCourseSet: (value: React.SetStateAction<Set<number>>) => void;
}) {
	return (
		<CoreTable
			columns={[
				{
					header: (
						<CoreCheckbox
							checked={selectedCourseSet.size > 0}
							indeterminate={selectedCourseSet.size > 0 && selectedCourseSet.size !== courseList.length}
							onChange={(e) => setSelectedCourseSet(new Set(e.target.checked ? courseList.map((x) => x.id) : []))}
							sx={{ padding: "2px" }}
							size={"15px"}
						/>
					),
					cell: (row) => (
						<CoreCheckbox
							checked={selectedCourseSet.has(row.data.id)}
							onChange={(e) =>
								setSelectedCourseSet(
									(s) => new Set(e.target.checked ? s.add(row.data.id) : s.delete(row.data.id) ? s : s),
								)
							}
							sx={{ padding: "2px" }}
							size={"15px"}
						/>
					),
					width: "20px",
					key: "checkbox",
				},
				{
					header: i18n.t("portal.components.settings.suffix.IndexTSX.courseID"),
					cell: (row) => row.data.courseId,
					key: "courseId",
					descendingComparator: (a, b) => b.courseId.toLowerCase().localeCompare(a.courseId.toLowerCase()),
					truncateString: true,
					align: "left",
					width: "140px",
				},
				{
					header: i18n.t("portal.components.settings.suffix.IndexTSX.courseName"),
					cell: (row) => `${row.data.name}${row.data.code ? ` (${row.data.code})` : ""}`,
					key: "courseName",
					descendingComparator: (a, b) =>
						`${b.name}${b.code ? ` (${b.code})` : ""}`
							.toLowerCase()
							.localeCompare(`${a.name}${a.code ? ` (${a.code})` : ""}`.toLowerCase()),
					truncateString: true,
					align: "left",
					width: "380px",
				},
			]}
			data={courseList}
			maxHeight="496px"
			stickyHeader
			rowsPerPage={courseList.length}
			hidePaginationOnSinglePage
			padding="0px 12px"
			initialSortKey="courseId"
			initialSortOrder="asc"
			sortRequired
			fontSize="15px"
			headerFontSize="15px"
		/>
	);
}

function CourseAssessmentViewTable({
	courseList,
	selectedCourseSet,
	courseAssessmentsMap,
	loading,
}: {
	courseList: Course[];
	selectedCourseSet: Set<number>;
	courseAssessmentsMap: Map<number, CourseAssessmentNameResponses>;
	loading: boolean;
}) {
	return (
		<CoreTable
			columns={[
				{
					header: i18n.t("portal.components.settings.suffix.IndexTSX.courseID"),
					cell: (row) => row.data.courseId,
					key: "courseId",
					descendingComparator: (a, b) => b.courseId.toLowerCase().localeCompare(a.courseId.toLowerCase()),
					tableSortLabelDisabled: loading,
					align: "left",
					truncateString: true,
					width: "140px",
				},
				{
					header: i18n.t("portal.components.settings.suffix.IndexTSX.courseName"),
					cell: (row) => `${row.data.name}${row.data.code ? ` (${row.data.code})` : ""}`,
					key: "courseName",
					descendingComparator: (a, b) =>
						`${b.name}${b.code ? ` (${b.code})` : ""}`
							.toLowerCase()
							.localeCompare(`${a.name}${a.code ? ` (${a.code})` : ""}`.toLowerCase()),
					tableSortLabelDisabled: loading,
					align: "left",
					truncateString: true,
					width: "380px",
				},
				{
					header: i18n.t("portal.components.settings.suffix.IndexTSX.completedAssessments"),
					cell: (row) => {
						const assessmentResponses = courseAssessmentsMap.get(row.data.id);
						if (assessmentResponses) {
							return (
								<span style={{ fontVariantNumeric: "tabular-nums" }}>
									{`${Object.values(assessmentResponses.assessments).filter((x) => x.ok).length}/${Object.keys(assessmentResponses.assessments).length}`}
								</span>
							);
						}
						return <CoreSpinner size="14px" />;
					},
					key: "view",
					align: "left",
					truncateString: true,
					width: "264px",
				},
			]}
			data={courseList
				.filter((x) => selectedCourseSet.has(x.id))
				.sort((a, b) => a.courseId.toLowerCase().localeCompare(b.courseId.toLowerCase()))}
			maxHeight="496px"
			stickyHeader
			rowsPerPage={courseList.length}
			hidePaginationOnSinglePage
			padding="0px 12px"
			initialSortKey="courseId"
			sortRequired
			fontSize="15px"
			headerFontSize="15px"
		/>
	);
}

function WarningContainer({ children }: { children: ReactNode }) {
	return (
		<div
			style={{
				borderRadius: "7px",
				border: "solid 2px #bd8700",
				backgroundColor: "#bd87000b",
				display: "flex",
				gap: "12px",
				padding: "8px",
				alignItems: "center",
				lineHeight: "normal",
			}}
		>
			<Icon sx={{ height: "24px", width: "24px", "& path": { fill: "#bd8700" } }}>
				<WarningIcon height="24px" width="24px" />
			</Icon>
			{children}
		</div>
	);
}
